import { useEffect, useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminRole, AdminUser, AdminUserStatus } from '@mds/shared/types/admin';

import AdminUserEditor from '@/components/administration/admin-user-editor';
import AdminUserRecord from '@/components/administration/admin-user-record';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import { controlCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Select from '@mds/shared/ui/select';
import { Table, Row, Cell } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import * as admin from '@/administration/administration.api';
import { ADMIN_STATUSES, statusTone } from '@/administration/status-tone';
import { useSession } from '@/auth/use-session';
import { stamp } from '@mds/shared/lib/dates';

const ALL = 'All';
const PAGE_SIZE = 25;

/**
 * The administrator directory.
 *
 * Filtering and paging happen server-side, so every control here is a query
 * parameter rather than a filter over a loaded array — the list is expected to
 * outgrow one page.
 */
export default function AdministratorsPage() {
  const queryClient = useQueryClient();
  const { can, session } = useSession();
  const mayManage = can('admin.users.manage');

  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>(ALL);
  const [roleId, setRoleId] = useState<string>(ALL);

  const [record, setRecord] = useState<AdminUser | null>(null);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);

  // Debounced so a typed search does not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => admin.listRoles(),
  });

  const users = useQuery({
    queryKey: ['admin', 'users', { page, search, status, roleId }],
    queryFn: () =>
      admin.listUsers({
        page,
        limit: PAGE_SIZE,
        q: search || undefined,
        status: status === ALL ? undefined : (status as AdminUserStatus),
        roleId: roleId === ALL ? undefined : roleId,
      }),
  });

  const roleOptions = useMemo(
    () => [
      { value: ALL, label: 'All roles' },
      ...(roles.data ?? []).map((r: AdminRole) => ({ value: r.id, label: r.name })),
    ],
    [roles.data],
  );

  /** Anything that changed a user invalidates the list and the open record. */
  const refresh = async (updated?: AdminUser) => {
    if (updated) setRecord((cur) => (cur && cur.id === updated.id ? updated : cur));
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  // The open record is re-read on its own: roles, status and sessions change
  // from other screens and other administrators, and editing should start from
  // the server's copy rather than the list page it was opened from. Keyed under
  // the list so `refresh` re-reads both.
  const single = useQuery({
    queryKey: ['admin', 'users', 'one', record?.id],
    queryFn: () => admin.getUser(record?.id as string),
    enabled: record !== null,
    placeholderData: record ?? undefined,
  });
  const shown = record ? (single.data ?? record) : null;

  const rows = users.data?.data ?? [];
  const pagination = users.data?.pagination;

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={pagination ? `${pagination.total} administrators` : 'Administrators'}
        action={
          <Toolbar>
            <input
              placeholder="Search name, email or phone"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search administrators"
              maxLength={100}
              className={controlCls + ' w-[260px]'}
            />
            <Select
              value={status}
              onChange={(next) => {
                setStatus(next);
                setPage(1);
              }}
              options={[ALL, ...ADMIN_STATUSES].map((s) => ({ value: s, label: s }))}
              aria-label="Filter by status"
            />
            <Select
              value={roleId}
              onChange={(next) => {
                setRoleId(next);
                setPage(1);
              }}
              options={roleOptions}
              aria-label="Filter by role"
            />
            {mayManage && <Button onClick={() => setCreating(true)}>Add administrator</Button>}
          </Toolbar>
        }
      >
        {users.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading administrators…</p>
        ) : users.isError ? (
          <EmptyState
            title="Could not load administrators"
            hint={
              users.error instanceof ApiError
                ? users.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No administrator matches"
            hint="Try a different search, or clear the status and role filters."
          />
        ) : (
          <>
            <Table head={['Name', 'Email', 'Roles', 'Status', 'Last sign-in', '']}>
              {rows.map((u) => (
                <Row key={u.id} onClick={() => setRecord(u)}>
                  <Cell className="font-semibold">{u.fullName ?? '—'}</Cell>
                  <Cell>
                    <div className="flex flex-col">
                      <span>{u.email}</span>
                      {u.phone && (
                        <span className="font-mono text-md text-ink-muted">{u.phone}</span>
                      )}
                    </div>
                  </Cell>
                  <Cell>
                    <div className="flex flex-wrap gap-1.5">
                      {u.roles.map((r) => (
                        <Chip key={r.id} className="text-md">
                          {r.name}
                        </Chip>
                      ))}
                    </div>
                  </Cell>
                  <Cell>
                    <Chip className={'border ' + statusTone(u.status)}>{u.status}</Chip>
                  </Cell>
                  <Cell className="font-mono text-md">{stamp(u.lastLoginAt)}</Cell>
                  <Cell>
                    {u.id === session?.user.id && (
                      <Chip className="border border-brand-edge/35 text-brand-strong">You</Chip>
                    )}
                  </Cell>
                </Row>
              ))}
            </Table>

            {pagination && pagination.totalPages > 1 && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-md text-ink-muted">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="quiet"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={!pagination.hasMore}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        open={!!record}
        onClose={() => setRecord(null)}
        title={shown?.fullName ?? shown?.email ?? ''}
        footer={
          shown ? (
            <>
              {mayManage && (
                <Button
                  onClick={() => {
                    setEditing(shown);
                    setRecord(null);
                  }}
                >
                  Edit
                </Button>
              )}
              <Button variant="quiet" onClick={() => setRecord(null)}>
                Close
              </Button>
            </>
          ) : null
        }
      >
        {shown && (
          <AdminUserRecord
            key={shown.id}
            user={shown}
            roles={roles.data ?? []}
            canManage={mayManage}
            isSelf={shown.id === session?.user.id}
            onChanged={refresh}
          />
        )}
      </Modal>

      <AdminUserEditor
        open={creating || !!editing}
        user={editing}
        roles={roles.data ?? []}
        isSelf={editing?.id === session?.user.id}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={async (saved) => {
          setCreating(false);
          setEditing(null);
          await refresh(saved);
        }}
      />
    </div>
  );
}
